import React, { useState } from "react";
import { Zap, Box, Globe, Users, Target, Search } from "react-feather";
import DetailModal from "./DetailModal";

const MarketPosition = () => {
  const [selectedHeatMapItem, setSelectedHeatMapItem] = useState(null);
  const [selectedDetailModal, setSelectedDetailModal] = useState(null);

  const heatMapData = {
    technologies: [
      {
        name: "Water-Based",
        players: 4,
        companies: ["Henkel", "H.B. Fuller", "Bostik", "Tailored"],
        color: "custom-danger",
        marketSize: "$18.5B",
        dataKey: "tech-0",
      },
      {
        name: "Hot-Melt",
        players: 4,
        companies: ["Henkel", "H.B. Fuller", "Bostik", "Tailored"],
        color: "custom-danger",
        marketSize: "$14.2B",
        dataKey: "tech-1",
      },
      {
        name: "Pressure-Sensitive",
        players: 2,
        companies: ["Henkel", "Bostik"],
        color: "custom-warning",
        marketSize: "$9.2B",
        dataKey: "tech-3",
      },
      {
        name: "Reactive (PUR)",
        players: 3,
        companies: ["Henkel", "H.B. Fuller", "Bostik"],
        color: "custom-info",
        marketSize: "$8.6B",
        dataKey: "tech-2",
      },
    ],
    applications: [
      {
        name: "Packaging",
        players: 4,
        companies: ["Henkel", "H.B. Fuller", "Bostik", "Tailored"],
        color: "custom-danger",
        marketSize: "$12.5B",
        dataKey: "app-1",
      },
      {
        name: "Construction",
        players: 4,
        companies: ["Henkel", "H.B. Fuller", "Bostik", "Tailored"],
        color: "custom-danger",
        marketSize: "$10.8B",
        dataKey: "app-0",
      },
      {
        name: "Woodworking",
        players: 4,
        companies: ["Tailored", "Henkel", "H.B. Fuller", "Bostik"],
        color: "custom-danger",
        marketSize: "$4.2B",
        dataKey: "app-4",
      },
      {
        name: "Hygiene",
        players: 2,
        companies: ["Henkel", "H.B. Fuller"],
        color: "custom-info",
        marketSize: "$3.8B",
        dataKey: "app-2",
      },
      {
        name: "Air Filtration",
        players: 1,
        companies: ["H.B. Fuller"],
        color: "custom-success",
        note: "HBF Leadership",
        marketSize: "$2.1B",
        dataKey: "app-3",
      },
    ],
    geographies: [
      {
        name: "Asia Pacific",
        players: 3,
        companies: ["Henkel", "H.B. Fuller", "Bostik"],
        color: "custom-info",
        marketSize: "$22.4B",
        dataKey: "geo-2",
      },
      {
        name: "Europe",
        players: 3,
        companies: ["Henkel", "H.B. Fuller", "Bostik"],
        color: "custom-info",
        marketSize: "$16.8B",
        dataKey: "geo-1",
      },
      {
        name: "North America",
        players: 4,
        companies: ["Henkel", "H.B. Fuller", "Bostik", "Tailored"],
        color: "custom-danger",
        marketSize: "$15.2B",
        dataKey: "geo-0",
      },
    ],
    segments: [
      {
        name: "Packaging Converters",
        players: 2,
        companies: ["H.B. Fuller", "Bostik"],
        color: "custom-warning",
        marketSize: "$12.5B",
        dataKey: "seg-2",
      },
      {
        name: "Construction Contractors",
        players: 3,
        companies: ["Henkel", "H.B. Fuller", "Bostik"],
        color: "custom-info",
        marketSize: "$10.8B",
        dataKey: "seg-0",
      },
      {
        name: "Automotive OEMs",
        players: 2,
        companies: ["Henkel", "H.B. Fuller"],
        color: "custom-warning",
        marketSize: "$8.2B",
        dataKey: "seg-1",
      },
      {
        name: "Wood Manufacturers",
        players: 1,
        companies: ["Tailored"],
        color: "custom-success",
        note: "Tailored Niche",
        marketSize: "$4.2B",
        dataKey: "seg-4",
      },
      {
        name: "Hygiene Manufacturers",
        players: 1,
        companies: ["H.B. Fuller"],
        color: "custom-success",
        note: "HBF Leader",
        marketSize: "$3.8B",
        dataKey: "seg-3",
      },
    ],
  };

  const focusAreas = {
    henkel: {
      technologies: [
        "Water-Based",
        "Hot-Melt (EVA, PUR)",
        "Reactive (Epoxy, PUR)",
      ],
      applications: [
        "Building & Construction",
        "Automotive & Transportation",
        "Electronics",
      ],
      geographies: ["Europe (HQ)", "North America", "Asia Pacific"],
      segments: [
        "Automotive OEMs",
        "Construction Contractors",
        "Electronics Manufacturers",
      ],
      useCases: [
        "EV Battery Assembly",
        "Structural Automotive Bonding",
        "Electronics Thermal Management",
      ],
    },
    hbfuller: {
      technologies: [
        "Water-Based",
        "Hot-Melt (EVA, APAO, PUR)",
        "Reactive PUR",
      ],
      applications: ["Hygiene & Nonwovens", "Air Filtration", "Packaging"],
      geographies: [
        "North America (Primary)",
        "Europe",
        "Asia Pacific (expanding)",
      ],
      segments: [
        "Hygiene Product Manufacturers",
        "Filter OEMs",
        "Packaging Converters",
      ],
      useCases: [
        "Diaper Elastic Bonding",
        "V-Cell Filter Assembly",
        "HEPA Filter Potting",
      ],
    },
    bostik: {
      technologies: [
        "Water-Based",
        "Hot-Melt PSA",
        "Pressure-Sensitive (Ashland)",
      ],
      applications: ["Building & Construction", "Flooring", "Packaging"],
      geographies: ["Europe (HQ)", "North America", "Asia Pacific (India)"],
      segments: [
        "Construction Contractors",
        "Building Material Suppliers",
        "Label/Tape Manufacturers",
      ],
      useCases: ["Flooring Installation", "Tile Adhesives", "Roofing Sealants"],
    },
    tailored: {
      technologies: ["Water-Based", "Hot-Melts", "Emulsion Polymers"],
      applications: ["Woodworking", "Furniture", "Packaging"],
      geographies: [
        "United States only",
        "North Carolina (4 facilities)",
        "Regional focus",
      ],
      segments: [
        "Wood Products Manufacturers",
        "Furniture Makers",
        "Cabinet Manufacturers",
      ],
      useCases: [
        "Plywood Assembly",
        "Furniture Edge Banding",
        "MDF Panel Bonding",
      ],
    },
  };

  const companies = [
    { id: "henkel", name: "Henkel" },
    { id: "hbfuller", name: "H.B. Fuller" },
    { id: "bostik", name: "Bostik" },
    { id: "tailored", name: "Tailored Chemical" },
  ];

  const renderHeatMapItems = (items) => {
    return items.map((item, idx) => {
      const itemKey = item.dataKey || `item-${idx}`;
      const isSelected = selectedHeatMapItem === itemKey;

      return (
        <div key={idx} className="mb-3">
          <div
            className={`heatmap-item bg-${
              item.color
            } bg-opacity-10 border-start border-${
              item.color
            } border-4 p-2 rounded cursor-pointer ${
              isSelected ? "shadow selected-item" : ""
            }`}
            onClick={() => setSelectedHeatMapItem(isSelected ? null : itemKey)}
            style={{ cursor: "pointer" }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="flex-grow-1">
                <div className="comp_head_new">{item.name}</div>
                <div className="comp_subhead_new">
                  {item.players} player{item.players > 1 ? "s" : ""} •{" "}
                  {item.players === 4
                    ? "Very crowded"
                    : item.players === 3
                    ? "Competitive"
                    : item.players === 2
                    ? "Moderate"
                    : item.note || "Unique"}
                </div>
                {item.marketSize && (
                  <div className="comp_head_number">
                    {item.marketSize}
                  </div>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDetailModal(itemKey);
                }}
                className="btn btn-sm btn-link p-0 ms-2"
                title="Click for detailed analysis"
                style={{ fontSize: "1.2rem" }}
              >
                <Search size={18} className="text-primary" />
              </button>
            </div>
          </div>

          {isSelected && (
            <div className="mt-2 p-2 bg-white rounded border border-primary border-1 shadow-sm">
              <div className="companies_competing_head mb-2">
                Companies competing here:
              </div>
              <div className="d-flex flex-wrap gap-1">
                {item.companies.map((company, compIdx) => (
                  <span
                    key={compIdx}
                    className={`badge fw-normal ${
                      company === "H.B. Fuller"
                        ? "bg-primary"
                        : company === "Henkel"
                        ? "bg-danger"
                        : company === "Bostik"
                        ? "bg-info"
                        : "bg-secondary"
                    }`}
                  >
                    {company}
                  </span>
                ))}
              </div>
              {item.note && (
                <div className="small text-primary fst-italic mt-2">
                  💡 {item.note}
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div>
      <style>{`
     
        .selected-item {
          transform: scale(1.05);
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .focus-badge {
          font-size: 0.7rem;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          display: inline-block;
          margin: 0.15rem;
        }
        .comp_map_block{
          border-bottom: 1px solid #e9ecef;
          background-color: #fff;
          padding: 12px 16px;
          border: solid 1px #98a4aa33;
          border-radius: 8px;
          margin-bottom: 10px;
        }
        .comp_subhead{
          font-size:13px;
          font-weight:600 !Important;
        }
        .comp_head_new{
          color:#2d343d !important;
          font-size:12px;
          line-height:17.5px;
          font-weight:600;
          margin-bottom:5px;
        }
        .comp_subhead_new{
          color:#64748B !important;
          font-size:12px;
          line-height:17.5px;
          font-weight:400;
          margin-bottom:5px;

        }
        .comp_head_number{
          color:#363b4b !important;
          font-size:13px;
          line-height:17.5px;
          font-weight:600;
        }
        .bg-custom-danger{
          background-color: #fef2f2;
          border-left: 4px solid #b91c1c !important;
        }
        .bg-custom-danger-only{
          background-color: #b91c1c;
        }
        .bg-custom-info{
          background-color: #eff6ff;
          border-left: 4px solid #1e74e6 !important;
        }
        .bg-custom-info-only{
          background-color: #1e74e6;
        }
        .bg-custom-success{
          background-color: #f0fdf4;
          border-left: 4px solid #15803d !important;
        }
        .bg-custom-success-only{
          background-color: #15803d;
        }
        .bg-custom-warning-only{
          background-color: #CFAB69;
        }
        .bg-custom-warning{
          background-color: #feebc89c;
          border-left: 4px solid #CFAB69 !important;
        }
        .score_inter{
          font-weight: 400;
    font-size: 12px;
    background-color: #f0f4fa;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.6);
    /* margin-top: 5px; */
    margin-bottom: 20px;
    padding: 12px 8px 12px 12px;
    border-radius: 8px;
        }
        .custom-resizable-table-th-content {
          justify-content: unset;
      }
      .custom_highlight_table{
        background-color:#f0f4fa;
      }
      .companies_competing_head{
        font-size: 13px;
        font-weight: 500;
        margin-bottom: 5px;
        line-height: 1;
        color: #2d343d;
      }
        
      `}</style>

      <DetailModal
        selectedDetailModal={selectedDetailModal}
        setSelectedDetailModal={setSelectedDetailModal}
      />

      {/* Competition Heat Map */}
      <div className="card border-0 mb-4 ">
        <div className="card-body comp_map_block">
          <h3 className="exe_summary mb-4">Competition Heat Map</h3>

          <div className="row g-3">
            <div className="col-md-3">
              <div className="d-flex align-items-center gap-2 comp_subhead mb-3">
                <Zap size={16} />
                Technologies
              </div>
              {renderHeatMapItems(heatMapData.technologies)}
            </div>

            <div className="col-md-3">
              <div className="d-flex align-items-center gap-2 comp_subhead fw-bold small mb-3">
                <Box size={16} />
                Applications
              </div>
              {renderHeatMapItems(heatMapData.applications)}
            </div>

            <div className="col-md-3">
              <div className="d-flex align-items-center gap-2 comp_subhead fw-bold small mb-3">
                <Globe size={16} />
                Geographies
              </div>
              {renderHeatMapItems(heatMapData.geographies)}
            </div>

            <div className="col-md-3">
              <div className="d-flex align-items-center gap-2 comp_subhead fw-bold small mb-3">
                <Users size={16} />
                Customer Segments
              </div>
              {renderHeatMapItems(heatMapData.segments)}
            </div>
          </div>

          <div className="row g-2 p-3 bg-light rounded mt-3">
            <div className="col-3 mt-0">
              <div className="d-flex align-items-center gap-2 small">
                <div
                  className="bg-custom-danger-only"
                  style={{ width: "1rem", height: "1rem", borderRadius: "2px" }}
                ></div>
                <div>
                  <div className="fw-bold font13">Very Crowded</div>
                  <div className="text-muted font13">4 players</div>
                </div>
              </div>
            </div>
            <div className="col-3 mt-0">
              <div className="d-flex align-items-center gap-2 small">
                <div
                  className="bg-custom-info-only"
                  style={{ width: "1rem", height: "1rem", borderRadius: "2px" }}
                ></div>
                <div>
                  <div className="fw-bold font13">Competitive</div>
                  <div className="text-muted font13">3 players</div>
                </div>
              </div>
            </div>
            <div className="col-3 mt-0">
              <div className="d-flex align-items-center gap-2 small">
                <div
                  className="bg-custom-warning-only"
                  style={{ width: "1rem", height: "1rem", borderRadius: "2px" }}
                ></div>
                <div>
                  <div className="fw-bold font13">Moderate</div>
                  <div className="text-muted font13">2 players</div>
                </div>
              </div>
            </div>
            <div className="col-3 mt-0">
              <div className="d-flex align-items-center gap-2 small">
                <div
                  className="bg-custom-success-only"
                  style={{ width: "1rem", height: "1rem", borderRadius: "2px" }}
                ></div>
                <div>
                  <div className="fw-bold font13">Unique/Leader</div>
                  <div className="text-muted font13">1 player</div>
                </div>
              </div>
            </div>
          </div>

          <div className="score_inter mt-2 mb-2">
            <div className="d-flex gap-2">
              {/* <Target size={20} className="flex-shrink-0 mt-1" /> */}
              <img src="/img/bullseye.png" width="16" height="16"/>
              <div className="small">
                <strong>Strategic Heat Map Insights:</strong> Packaging ($12.5B)
                and Construction ($10.8B) are largest applications but highly
                competitive (4 players each).{" "}
                <strong>
                  CRITICAL: Asia Pacific ($22.4B, 7.3% CAGR) is THE growth
                  opportunity.
                </strong>{" "}
                <strong>
                  GREEN OCEANS: Air Filtration ($2.1B, 52% H.B. Fuller share)
                  and Hygiene Manufacturing ($3.8B, 48% share) offer protected
                  positions.
                </strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Where Each Company Focuses */}
      <div className="card border-0  mb-4">
        <div className="card-body comp_map_block">
          <h3 className="exe_summary mb-4">Where Each Company Focuses</h3>
          <div className="table-responsive">
            <table className="table custom-resizable-table">
              <thead>
                <tr className="custom-resizable-table-header">
                  <th className=""><div className="custom-resizable-table-th-content">Company</div></th>
                  <th className="">
                    <div className="d-flex align-items-center gap-1 custom-resizable-table-th-content">
                      <Zap size={14} />
                      Top Technologies
                    </div>
                  </th>
                  <th className="">
                    <div className="d-flex align-items-center gap-1 custom-resizable-table-th-content">
                      <Box size={14} />
                      Top Applications
                    </div>
                  </th>
                  <th className="">
                    <div className="d-flex align-items-center gap-1 custom-resizable-table-th-content">
                      <Globe size={14} />
                      Top Geographies
                    </div>
                  </th>
                  <th className="">
                    <div className="d-flex align-items-center gap-1 custom-resizable-table-th-content">
                      <Users size={14} />
                      Top Segments
                    </div>
                  </th>
                  <th className="">
                    <div className="d-flex align-items-center gap-1 custom-resizable-table-th-content">
                      <Target size={14} />
                      Key Use Cases
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr
                    key={company.id}
                    className={ 
                      company.id === "hbfuller"
                        ? "table-primary border-start border-primary border-2 custom_highlight_table"
                        : ""
                    }
                    gtr-row
                  >
                    <td className="gtr-cell">
                      <div className="d-flex align-items-center gap-2">
                        {company.id === "hbfuller" && (
                          <span className="badge bg-primary">US</span>
                        )}
                        {company.name}
                      </div>
                    </td>
                    <td>
                      <div>
                        {focusAreas[company.id].technologies.map(
                          (tech, techIdx) => (
                            <span
                              key={techIdx}
                              className="focus-badge bg-purple bg-opacity-10 text-dark"
                            >
                              {tech}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        {focusAreas[company.id].applications.map(
                          (app, appIdx) => (
                            <span
                              key={appIdx}
                              className="focus-badge bg-primary bg-opacity-10 text-dark"
                            >
                              {app}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        {focusAreas[company.id].geographies.map(
                          (geo, geoIdx) => (
                            <span
                              key={geoIdx}
                              className="focus-badge bg-success bg-opacity-10 text-dark"
                            >
                              {geo}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        {focusAreas[company.id].segments.map((seg, segIdx) => (
                          <span
                            key={segIdx}
                            className="focus-badge bg-warning bg-opacity-10 text-dark"
                          >
                            {seg}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div>
                        {focusAreas[company.id].useCases.map(
                          (useCase, ucIdx) => (
                            <span
                              key={ucIdx}
                              className="focus-badge bg-danger bg-opacity-10 text-dark"
                            >
                              {useCase}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketPosition;
